export const retryCategoriesV1021 = ['language', 'image', 'video', 'other'];
export const retryFieldsV1021 = ['same_source_retries', 'fallback_retries'];

export const defaultRetrySettingsV1021 = () => ({
  language: { same_source_retries: 1, fallback_retries: 0 },
  image: { same_source_retries: 1, fallback_retries: 2 },
  video: { same_source_retries: 0, fallback_retries: 0 },
  other: { same_source_retries: 0, fallback_retries: 0 },
});

export function validRetrySettingsV1021(settings) {
  return Boolean(
    settings &&
      typeof settings === 'object' &&
      !Array.isArray(settings) &&
      Object.keys(settings).length === retryCategoriesV1021.length &&
      retryCategoriesV1021.every((category) => {
        const row = settings[category];
        return (
          row &&
          typeof row === 'object' &&
          !Array.isArray(row) &&
          Object.keys(row).length === retryFieldsV1021.length &&
          retryFieldsV1021.every(
            (field) =>
              Number.isInteger(row[field]) && row[field] >= 0 && row[field] <= 5
          )
        );
      })
  );
}

const labels = {
  en: {
    title: 'Fallback & retry',
    enabled: 'On',
    disabled: 'Off',
    toggle: 'Enable Fallback & retry',
    toggleHelp:
      'Off sends only the initial request. Saved retry counts are retained.',
    billing:
      'Same-source and Fallback retries aim to improve success. There is no extra retry fee or separate customer charge for each attempt in the same request.',
    billingBasis:
      'The final charge follows the applicable Source price and actual billable usage; switching Source may change the price. Safety limits still apply and success is not guaranteed.',
    defaults:
      'New Keys and older Keys without a saved switch setting are on by default. Explicitly disabled switches and saved retry counts are preserved.',
    language: 'Language models',
    image: 'Image generation',
    video: 'Video models',
    other: 'Other models',
    same_source_retries: 'Same-source retries',
    fallback_retries: 'Fallback retries',
    help: 'Retry sequence help',
    sequence:
      'These counts apply only to this Key and are saved with it. The first request uses the Source selected by its routing strategy. Same-source retries resend to that original logical Source: 1 means one extra request, excluding the initial request. Only after those attempts fail does fallback begin. Each fallback selects an untried Source at the lowest eligible multiplier strictly above the previous attempt. Equal or lower multipliers are skipped. Each fallback Source is dispatched at most once; its same-source budget is not restarted. With 2 same-source retries and 3 fallback retries, the sequence is A, A, A, B, C, D: at most 6 total attempts, with increasing multipliers from A through D. Success stops immediately. Exhausted counts or no eligible higher Source end the request. The model ID stays the same.',
    safety:
      'Every attempt remains subject to Source permissions, selected groups, capabilities and the price ceiling. Direct-tariff and receipt-priced Sources cannot be crossed within one request. Cancellation, accepted output and an unknown image or video submission outcome stop retries to avoid duplicate work. A retry count is a ceiling, not a guarantee.',
    legacy: 'Legacy retry mode',
    legacyCount: 'Legacy retry limit',
    upgrade: 'Use categorized retries',
    legacyHelp:
      'This Key retains its explicit legacy total retry limit until you choose Use categorized retries. When priced Source fallback is available, each fallback Source is attempted once and its multiplier must strictly increase. Editing the routing strategy does not upgrade the budget. Upgrading replaces the old limit with the four-category defaults; it is applied when the Key is saved.',
    allowFallback: 'Allow switching Source',
    permissionHelp: 'Source switching limit help',
    permission:
      'Turning this off caps fallback retries at 0 for every category on this Key. Stored category counts are retained. Turning it on does not expand Source permissions, selected Source lists, groups or price ceilings.',
    masterOff:
      'Retries are off. Source fallback is inactive; enable Fallback & retry to use the saved settings.',
    imageDefaults:
      'Image defaults: 1 same-source retry and 2 Source fallbacks, up to 4 sends including the initial request.',
    blocked: 'Effective fallback: 0',
    range:
      'Only whole numbers from 0 to 5 are accepted. The displayed value is unchanged.',
    invalid:
      'The saved category policy is invalid. Reset the category defaults before saving this Key.',
    reset: 'Reset category defaults',
  },
  zh: {
    title: 'Fallback 与重试',
    enabled: '已开启',
    disabled: '已关闭',
    toggle: '启用 Fallback 与重试',
    toggleHelp: '关闭后仅发送首次请求，已设置的重试次数仍保留。',
    billing:
      '同源与 Fallback 重试用于提高成功率，不收取额外重试费，同一请求不会因多次尝试而逐次重复扣费。',
    billingBasis:
      '最终费用按适用 Source 价格和实际计费用量结算；切换 Source 后价格可能不同。重试仍受安全限制，不保证成功。',
    defaults:
      '新 Key 和未保存开关设置的旧 Key 默认开启；明确关闭的开关及已设置的重试次数保持不变。',
    language: '语言模型',
    image: '图像生成模型',
    video: '视频模型',
    other: '其他模型',
    same_source_retries: '同源重试次数',
    fallback_retries: 'Fallback 重试次数',
    help: '重试顺序说明',
    sequence:
      '这些次数仅作用于当前 Key，随 Key 一起保存。首次请求按路由策略选择 Source。同源重试向最初选中的同一个逻辑 Source 重新发送请求：设置 1 次表示首次请求之外再请求 1 次。同源次数内均失败后才进入 fallback，每次选择尚未尝试、倍率严格高于上一次的最低合格档位；跳过相同或更低倍率。每个后备 Source 最多实际发送一次，切换 Source 后不会重新获得同源次数。例如同源 2 次、fallback 3 次，依次请求 A、A、A、B、C、D，最多总计尝试 6 次，A 至 D 的倍率逐级提高。任一次成功即停止；次数耗尽或没有更高倍率的合格 Source 时返回失败。模型 ID 始终不变。',
    safety:
      '每次尝试仍受 Source 权限、所选分组、能力和价格上限约束。同一请求不能在固定价目与回执计价 Source 之间跨结算方式切换。请求取消、已接收输出，以及图像或视频提交结果未知时，会停止重试，避免重复生成。设置次数是上限，不保证一定重试。',
    legacy: '旧版重试模式',
    legacyCount: '旧版重试总上限',
    upgrade: '改用分类重试',
    legacyHelp:
      '此 Key 保留已明确设置的旧版重试总上限，直到主动选择改用分类重试。存在可切换的定价 Source 时，每个后备 Source 也只尝试一次，倍率必须严格递增。修改路由策略不会自动升级次数设置。升级会用四类模型默认值替换旧上限，在保存 Key 后生效。',
    allowFallback: '允许切换 Source',
    permissionHelp: 'Source 切换限制说明',
    permission:
      '关闭后，当前 Key 所有类型的 fallback 有效次数均为 0；已配置的分类次数仍保留。开启不会扩大 Source 权限、指定 Source 列表、分组或价格上限。',
    masterOff:
      '重试总开关已关闭，Source fallback 当前不生效。开启「Fallback 与重试」后使用已保存的设置。',
    imageDefaults:
      '图像预设：同源重试 1 次、Source fallback 2 次，含首次请求最多发送 4 次。',
    blocked: '有效 fallback：0',
    range: '仅接受 0 至 5 的整数，显示值未改变。',
    invalid: '已保存的分类策略无效。请先恢复分类默认值，再保存此 Key。',
    reset: '恢复分类默认值',
  },
};

export const retryTextV1021 = (language) =>
  labels[String(language || 'en').startsWith('zh') ? 'zh' : 'en'];
